// Hook for handling automatic upload resumption after login

import { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient.ts';
import { getCachedUpload, clearCachedUpload, cachedUploadToFile } from '../utils/uploadCache.ts';
import { getAndClearRedirectIntent } from '../utils/redirectIntent.ts';
import { uploadZipFile, uploadGithubRepo } from '../services/apiService.ts';
import { supabaseStatusStore } from '../utils/supabaseStatus.ts';

export function useUploadResumption() {
  const [isResuming, setIsResuming] = useState(false);
  const [resumptionError, setResumptionError] = useState<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[useUploadResumption] Auth state change:', event, !!session);
      
      if (event === 'SIGNED_IN' && session) {
        console.log('[useUploadResumption] User signed in, checking for cached uploads...');
        
        // Small delay to ensure auth state is settled
        setTimeout(async () => {
          await handleUploadResumption();
        }, 500);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Register retry logic with supabaseStatusStore if error is network/CORS
  useEffect(() => {
    if (resumptionError && (resumptionError.toLowerCase().includes('network') || resumptionError.toLowerCase().includes('cors'))) {
      supabaseStatusStore.setRetry(handleUploadResumption);
    } else {
      supabaseStatusStore.setRetry(() => {});
    }
    return () => {
      supabaseStatusStore.setRetry(() => {});
    };
  }, [resumptionError]);

  const handleUploadResumption = async () => {
    try {
      setIsResuming(true);
      setResumptionError(null);

      console.log('[useUploadResumption] Starting upload resumption process...');

      // Check for cached upload first (higher priority)
      let cachedUpload;
      try {
        cachedUpload = getCachedUpload();
      } catch (cacheError) {
        console.error('[useUploadResumption] ❌ Error reading cache:', cacheError);
        // Clear corrupted cache and continue
        try {
          clearCachedUpload();
          console.log('[useUploadResumption] ✅ Cleared corrupted cache');
        } catch (clearError) {
          console.error('[useUploadResumption] ❌ Failed to clear cache:', clearError);
        }
        cachedUpload = null;
      }
      
      if (!cachedUpload) {
        console.log('[useUploadResumption] No cached upload found');
        
        // Fallback: Check for redirect intent if no cached upload
        const redirectIntent = getAndClearRedirectIntent();
        if (redirectIntent) {
          console.log('[useUploadResumption] 🔄 No cached upload, but found redirect intent:', redirectIntent.path);
          window.location.href = redirectIntent.path;
          return;
        }

        console.log('[useUploadResumption] No cached upload or redirect intent found');
        return;
      }

      // Enhanced validation with defensive checks
      console.log('[useUploadResumption] 🔄 Detected cached upload:', {
        source_type: cachedUpload.source_type,
        scan_id: cachedUpload.scan_id,
        name: cachedUpload.name || cachedUpload.repoUrl,
        hasContents: !!cachedUpload.contents,
        hasRepoUrl: !!cachedUpload.repoUrl,
        keys: Object.keys(cachedUpload)
      });

      // Additional safety checks
      if (!cachedUpload.source_type || !cachedUpload.scan_id) {
        console.error('[useUploadResumption] ❌ Invalid cached upload structure, clearing cache');
        clearCachedUpload();
        throw new Error('Invalid cached upload data - cleared cache');
      }

      let realScanId: string;

      if (cachedUpload.source_type === 'zip') {
        console.log('[useUploadResumption] Resuming ZIP upload...');
        
        // Enhanced validation for ZIP uploads
        if (!cachedUpload.contents || !cachedUpload.name || typeof cachedUpload.contents !== 'string') {
          console.error('[useUploadResumption] ❌ Invalid ZIP cache data:', {
            hasContents: !!cachedUpload.contents,
            hasName: !!cachedUpload.name,
            contentsType: typeof cachedUpload.contents
          });
          clearCachedUpload();
          throw new Error('Invalid cached ZIP upload - missing or corrupted file data');
        }

        console.log('[useUploadResumption] Converting cached ZIP to File object...');
        let file;
        try {
          file = cachedUploadToFile(cachedUpload);
          console.log('[useUploadResumption] File object created successfully:', {
            name: file.name,
            size: file.size,
            type: file.type
          });
        } catch (conversionError) {
          console.error('[useUploadResumption] ❌ Failed to convert cached upload to file:', conversionError);
          clearCachedUpload();
          throw new Error('Failed to convert cached upload to file object');
        }
        
        console.log('[useUploadResumption] Uploading ZIP file to server...');
        const uploadResponse = await uploadZipFile(file);
        
        if (!uploadResponse.success || !uploadResponse.scan_id) {
          throw new Error('ZIP upload failed: ' + (uploadResponse.error || 'No scan ID returned'));
        }
        
        realScanId = uploadResponse.scan_id;
        console.log('[useUploadResumption] ✅ ZIP upload resumed successfully, scan_id:', realScanId);
        
      } else if (cachedUpload.source_type === 'github') {
        console.log('[useUploadResumption] Resuming GitHub upload...');
        
        // Enhanced validation for GitHub uploads
        if (!cachedUpload.repoUrl || typeof cachedUpload.repoUrl !== 'string') {
          console.error('[useUploadResumption] ❌ Invalid GitHub cache data:', {
            hasRepoUrl: !!cachedUpload.repoUrl,
            repoUrlType: typeof cachedUpload.repoUrl,
            repoUrl: cachedUpload.repoUrl
          });
          clearCachedUpload();
          throw new Error('Invalid cached GitHub upload - missing or invalid repo URL');
        }

        // Validate repoUrl format with stricter checks
        const githubUrlPattern = /^https:\/\/github\.com\/[a-zA-Z0-9._-]+\/[a-zA-Z0-9._-]+(\.git)?$/;
        if (!githubUrlPattern.test(cachedUpload.repoUrl)) {
          console.error('[useUploadResumption] ❌ Invalid GitHub URL format:', cachedUpload.repoUrl);
          clearCachedUpload();
          throw new Error('Invalid cached GitHub upload - malformed repo URL format');
        }

        // Additional safety checks for GitHub URLs
        if (cachedUpload.repoUrl.length < 20 || cachedUpload.repoUrl.length > 200) {
          console.error('[useUploadResumption] ❌ GitHub URL length invalid:', {
            url: cachedUpload.repoUrl,
            length: cachedUpload.repoUrl.length
          });
          clearCachedUpload();
          throw new Error('Invalid cached GitHub upload - suspicious URL length');
        }

        console.log('[useUploadResumption] GitHub repo URL validated:', cachedUpload.repoUrl);
        console.log('[useUploadResumption] Calling uploadGithubRepo with validated payload...');

        let uploadResponse;
        try {
          uploadResponse = await uploadGithubRepo({ repoUrl: cachedUpload.repoUrl });
          console.log('[useUploadResumption] GitHub upload response received:', {
            success: uploadResponse?.success,
            scan_id: uploadResponse?.scan_id,
            hasError: !!uploadResponse?.error
          });
        } catch (uploadError) {
          console.error('[useUploadResumption] ❌ GitHub upload API call failed:', uploadError);
          
          // Enhanced error handling for specific GitHub upload failures
          let friendlyError = 'GitHub upload failed';
          if (uploadError instanceof Error) {
            const errorMsg = uploadError.message.toLowerCase();
            if (errorMsg.includes('404') || errorMsg.includes('not found')) {
              friendlyError = 'Repository not found - make sure it\'s public and accessible';
            } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
              friendlyError = 'Network error while accessing GitHub - please try again';
            } else if (errorMsg.includes('timeout')) {
              friendlyError = 'GitHub download timed out - repository may be too large';
            } else {
              friendlyError = `GitHub upload failed: ${uploadError.message}`;
            }
          }
          
          throw new Error(friendlyError);
        }
        
        // Validate response structure
        if (!uploadResponse || typeof uploadResponse !== 'object') {
          throw new Error('GitHub upload failed: Invalid response from server');
        }
        
        if (!uploadResponse.success) {
          const errorMsg = uploadResponse.error || 'Unknown error from GitHub upload service';
          console.error('[useUploadResumption] ❌ GitHub upload unsuccessful:', errorMsg);
          throw new Error(`GitHub upload failed: ${errorMsg}`);
        }
        
        if (!uploadResponse.scan_id || typeof uploadResponse.scan_id !== 'string') {
          console.error('[useUploadResumption] ❌ Invalid scan_id in response:', uploadResponse.scan_id);
          throw new Error('GitHub upload failed: No valid scan ID returned from server');
        }
        
        realScanId = uploadResponse.scan_id;
        console.log('[useUploadResumption] ✅ GitHub upload resumed successfully, scan_id:', realScanId);
        
      } else {
        console.error('[useUploadResumption] ❌ Unsupported upload type:', cachedUpload.source_type);
        clearCachedUpload();
        throw new Error(`Unsupported upload type: ${cachedUpload.source_type}`);
      }

      // Final validation of scan ID before redirect
      if (!realScanId || typeof realScanId !== 'string' || realScanId.trim().length === 0) {
        console.error('[useUploadResumption] ❌ Invalid scan ID received:', realScanId);
        throw new Error('Upload completed but received invalid scan ID');
      }

      // Clear the cache since upload was successful
      console.log('[useUploadResumption] Clearing cache after successful upload...');
      clearCachedUpload();
      
      // Redirect to real results page
      console.log('[useUploadResumption] 🚀 Redirecting to real results page:', `/results/${realScanId}`);
      window.location.href = `/results/${realScanId}`;
      return;
      
    } catch (error) {
      console.error('[useUploadResumption] ❌ Upload resumption failed:', error);
      console.error('[useUploadResumption] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to resume upload';
      setResumptionError(errorMessage);
      
      // If running locally and it looks like a CORS / network error, clear cache so user isn\'t stuck
      if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('cors')) {
        console.warn('[useUploadResumption] Detected possible CORS/network error – clearing cached upload');
        try {
          clearCachedUpload();
        } catch {}
      }
      
      // Keep the cache otherwise in case of other retriable errors
      
    } finally {
      setIsResuming(false);
    }
  };

  return {
    isResuming,
    resumptionError,
    retryResumption: handleUploadResumption,
    clearError: () => setResumptionError(null),
    clearCacheAndError: () => {
      console.log('[useUploadResumption] Manual cache and error clearing');
      try {
        clearCachedUpload();
        setResumptionError(null);
        console.log('[useUploadResumption] ✅ Cache and error cleared successfully');
      } catch (error) {
        console.error('[useUploadResumption] ❌ Failed to clear cache:', error);
      }
    }
  };
} 